import React from 'react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Calendar as CalendarIcon, X } from 'lucide-react';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

/**
 * DatePicker reutilizável com locale pt-BR.
 *
 * Props:
 *  - value: string ISO ("YYYY-MM-DD" ou ISO completo). Vazio/null = sem data.
 *  - onChange: (newValueISO: string) => void  — recebe "YYYY-MM-DD" ou "" se limpo
 *  - placeholder: string (default "Selecionar data")
 *  - disabled, required, className, "data-testid": ...rest
 *  - allowClear: bool (default true) — exibe botão X para limpar
 *  - minDate, maxDate: Date objects opcionais
 */
const DatePicker = React.forwardRef(({
  value,
  onChange,
  placeholder = "Selecionar data",
  disabled = false,
  className,
  allowClear = true,
  minDate,
  maxDate,
  ...rest
}, ref) => {
  const dateObj = React.useMemo(() => {
    if (!value) return undefined;
    try {
      // Aceita "YYYY-MM-DD" ou ISO completo
      const iso = value.includes('T') ? value : `${value}T00:00:00`;
      const d = parseISO(iso);
      return isNaN(d.getTime()) ? undefined : d;
    } catch {
      return undefined;
    }
  }, [value]);

  const handleSelect = (d) => {
    if (!d) {
      onChange?.('');
      return;
    }
    // Always emit YYYY-MM-DD (consistente com inputs nativos type="date")
    onChange?.(format(d, 'yyyy-MM-dd'));
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange?.('');
  };

  const displayValue = dateObj
    ? format(dateObj, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })
    : '';

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          ref={ref}
          variant="outline"
          type="button"
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:text-gray-900",
            !dateObj && "text-gray-400",
            className,
          )}
          {...rest}
        >
          <CalendarIcon className="mr-2 h-4 w-4 text-gray-500 flex-shrink-0" />
          <span className="flex-1 truncate">{dateObj ? displayValue : placeholder}</span>
          {allowClear && dateObj && !disabled && (
            <span
              role="button"
              tabIndex={0}
              onClick={handleClear}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClear(e); }}
              className="ml-2 text-gray-400 hover:text-red-600 cursor-pointer p-0.5 rounded hover:bg-gray-100"
              data-testid="datepicker-clear"
              aria-label="Limpar data"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-white border-gray-200" align="start">
        <Calendar
          mode="single"
          selected={dateObj}
          onSelect={handleSelect}
          locale={ptBR}
          fromDate={minDate}
          toDate={maxDate}
          disabled={disabled}
          showOutsideDays
        />
      </PopoverContent>
    </Popover>
  );
});

DatePicker.displayName = 'DatePicker';
export default DatePicker;
